import React, { useState, useEffect, useRef, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import FactoryIcon from '@mui/icons-material/Factory';
import ExtensionIcon from '@mui/icons-material/Extension';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import { getClosingPriceTrend } from '../api/analyticsApi';
import * as departmentApi from '../api/departmentApi';
import * as materialGroupApi from '../api/materialGroupApi';
import * as materialApi from '../api/materialApi';
import * as makeApi from '../api/makeApi';
import * as priceApi from '../api/priceApi';
import { getStats } from '../api/statsApi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getCached } from '../utils/apiCache';

const COLORS = ['#1565C0', '#E65100', '#2E7D32', '#6A1B9A', '#00838F', '#AD1457', '#F57F17', '#4E342E'];

const StatCard = ({ icon, label, value, color }) => (
  <Card>
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '10px 14px !important' }}>
      <Box sx={{ bgcolor: color + '20', borderRadius: 1.5, p: 1, display: 'flex' }}>
        {React.cloneElement(icon, { sx: { color, fontSize: 22 } })}
      </Box>
      <Box>
        <Typography variant="h6" fontWeight={700} lineHeight={1.2}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </Box>
    </CardContent>
  </Card>
);

const fmt = (v) => `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const formatPeriodLabel = (value) => {
  if (!value) return '';
  try {
    const d = new Date(value);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return String(value); }
};

export default function Dashboard() {
  const [period, setPeriod] = useState('monthly');

  // Master data
  const [departments, setDepartments] = useState([]);
  const [allGroups, setAllGroups] = useState([]);   // all groups — for free group selection
  const [makes, setMakes] = useState([]);
  const [materialOptions, setMaterialOptions] = useState([]);
  const [materialSearch, setMaterialSearch] = useState('');

  // Filters
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null); // MANDATORY
  const [selectedMakes, setSelectedMakes] = useState([]);          // multi-select for line chart

  // Chart data
  const [closing, setClosing] = useState([]);
  const [currentPrices, setCurrentPrices] = useState([]); // latest price per make for bar chart
  const [chartLoading, setChartLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    departments: 0, materials: '...', accessories: 0, makes: 0, prices: 0,
    materialsLinked: 0, materialsUnlinked: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);

  const searchTimer = useRef(null);

  // Load stat counts + reference masters (all cached)
  useEffect(() => {
    const init = async () => {
      try {
        const [depts, grps, mk, statsData] = await Promise.all([
          getCached('departments:all', () => departmentApi.getAll()),
          getCached('materialGroups:all', () => materialGroupApi.getAll()),
          getCached('makes:all', () => makeApi.getAll()),
          getCached('stats', () => getStats(), 2 * 60 * 1000), // 2-min TTL for counts
        ]);
        setDepartments(Array.isArray(depts) ? depts : (depts.data || []));
        setAllGroups(Array.isArray(grps) ? grps : (grps.data || []));
        setMakes(Array.isArray(mk) ? mk : (mk.data || []));
        const sd = statsData.data ?? statsData;
        setStats({
          departments:       sd.departments       ?? 0,
          materials:         sd.materials         ?? 0,
          accessories:       sd.accessories       ?? 0,
          makes:             sd.makes             ?? 0,
          prices:            sd.prices            ?? 0,
          materialsLinked:   sd.materialsLinked   ?? 0,
          materialsUnlinked: sd.materialsUnlinked ?? 0,
        });
      } catch (e) { console.error(e); }
      finally { setStatsLoading(false); }
    };
    init();
  }, []);

  // Auto-populate dept when group selected without dept
  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
    setSelectedMaterial(null);
    if (group && !selectedDept) {
      const dept = departments.find((d) => d.id === group.departmentId);
      if (dept) setSelectedDept(dept);
    }
  };

  // Auto-populate dept+group when material selected
  const handleMaterialSelect = (material) => {
    setSelectedMaterial(material);
    setSelectedMakes([]);
    if (!material) return;
    const matGroup = material.materialGroup;
    const matDept = matGroup?.department;
    if (!selectedDept && matDept) {
      setSelectedDept(matDept);
      if (matGroup) setSelectedGroup(matGroup);
    } else if (selectedDept && !selectedGroup && matGroup) {
      setSelectedGroup(matGroup);
    }
  };

  // Groups shown in autocomplete: filtered by selected dept if any (memoized)
  const groupOptions = useMemo(() =>
    selectedDept ? allGroups.filter((g) => g.departmentId === selectedDept.id) : allGroups,
    [selectedDept, allGroups]
  );

  // Debounced server-side material search scoped by group/dept
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const params = { limit: 50 };
        if (materialSearch) params.search = materialSearch;
        if (selectedGroup) params.materialGroupId = selectedGroup.id;
        else if (selectedDept) params.departmentId = selectedDept.id;
        const res = await materialApi.getAll(params);
        setMaterialOptions(res.data || res);
      } catch (e) { console.error(e); }
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [materialSearch, selectedGroup, selectedDept]);

  // Makes available for the selected material (only those with prices)
  const availableMakes = selectedMaterial
    ? makes.filter((mk) =>
        currentPrices.some((p) => p.makeId === mk.id)
      )
    : makes;

  // Fetch charts + current prices when material selected
  useEffect(() => {
    if (!selectedMaterial) { setClosing([]); setCurrentPrices([]); return; }
    const fetch = async () => {
      setChartLoading(true);
      try {
        const [cp, latestPrices] = await Promise.all([
          getClosingPriceTrend({ period, materialId: selectedMaterial.id }),
          priceApi.getAll({ materialId: selectedMaterial.id }),
        ]);
        setClosing(cp.data || []);
        setCurrentPrices(latestPrices);
      } catch (e) { console.error(e); }
      finally { setChartLoading(false); }
    };
    fetch();
  }, [selectedMaterial, period]);

  // Series actually on screen — "All Makes" (no filter) or the explicitly
  // selected subset. Shared by the line chart and the "vs prev period" badge
  // so both always reflect the same selection.
  const filteredSeries = useMemo(() => (
    selectedMakes.length === 0
      ? closing
      : closing.filter((s) => selectedMakes.some((m) => m.id === s.makeId))
  ), [closing, selectedMakes]);

  // Build line chart data filtered by selected makes (memoized — only recomputes when data changes)
  const lineChart = useMemo(() => {
    if (!filteredSeries.length) return { data: [], keys: [] };
    const periodSet = new Set();
    filteredSeries.forEach((s) => s.data.forEach((d) => periodSet.add(String(d.period))));
    const periods = [...periodSet].sort();
    const data = periods.map((p) => {
      const row = { period: p, label: formatPeriodLabel(p) };
      filteredSeries.forEach((s) => {
        const pt = s.data.find((d) => String(d.period) === p);
        row[s.makeName] = pt ? pt.value : null;
      });
      return row;
    });
    return { data, keys: filteredSeries.map((s) => s.makeName) };
  }, [filteredSeries]);

  // Build current prices bar chart data (memoized)
  const currentPriceBarData = useMemo(() =>
    currentPrices.map((p) => ({
      make: p.make?.name || `Make ${p.makeId}`,
      price: Number(p.price),
      makeId: p.makeId,
    })),
    [currentPrices]
  );

  // Average of each currently-shown make's latest period-over-period change —
  // reflects the actual "All Makes" / filtered selection, not just whichever
  // make happens to come first in the API response.
  const latestChange = useMemo(() => {
    const changes = filteredSeries
      .map((s) => (s.data.length >= 2 ? s.data[s.data.length - 1].changePercent : null))
      .filter((c) => c !== null && c !== undefined);
    if (!changes.length) return null;
    return Number((changes.reduce((sum, c) => sum + c, 0) / changes.length).toFixed(2));
  }, [filteredSeries]);

  return (
    <Box>
      {/* ── Row 1: General counts ─────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { icon: <CategoryIcon />, label: 'Departments', value: statsLoading ? '...' : stats.departments, color: '#1565C0' },
          { icon: <InventoryIcon />, label: 'Materials', value: statsLoading ? '...' : stats.materials, color: '#2E7D32' },
          { icon: <ExtensionIcon />, label: 'Accessories', value: statsLoading ? '...' : stats.accessories, color: '#00838F' },
          { icon: <FactoryIcon />, label: 'Makes', value: statsLoading ? '...' : stats.makes, color: '#E65100' },
          { icon: <AttachMoneyIcon />, label: 'Active Prices', value: statsLoading ? '...' : stats.prices, color: '#6A1B9A' },
        ].map((s) => (
          <Grid item xs={12} sm={6} md={2.4} key={s.label}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>

      {/* ── Row 2: Material–Accessory linkage insights ───────────────────── */}
      <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#78909C', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', pl: 0.5 }}>
        Material – Accessory Linkage
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ border: '1px solid #B3E5FC', bgcolor: '#E1F5FE' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '10px 14px !important' }}>
              <Box sx={{ bgcolor: '#0288D120', borderRadius: 1.5, p: 1, display: 'flex' }}>
                <LinkIcon sx={{ color: '#0288D1', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1.2} color="#01579B">
                  {statsLoading ? '...' : stats.materialsLinked}
                </Typography>
                <Typography variant="caption" color="#0277BD">Materials Linked with Accessories</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ border: '1px solid #FFE0B2', bgcolor: '#FFF8F0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '10px 14px !important' }}>
              <Box sx={{ bgcolor: '#ED6C0220', borderRadius: 1.5, p: 1, display: 'flex' }}>
                <LinkOffIcon sx={{ color: '#ED6C02', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1.2} color="#E65100">
                  {statsLoading ? '...' : stats.materialsUnlinked}
                </Typography>
                <Typography variant="caption" color="#BF360C">Materials Not Linked with Accessories</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SearchIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={700}>Analytics Filters</Typography>
            <Typography variant="caption" color="error.main" sx={{ ml: 1 }}>
              * Select a Material to view charts
            </Typography>
          </Box>
          <Grid container spacing={2} alignItems="flex-start">
            {/* Department */}
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete size="small" options={departments} getOptionLabel={(o) => o.name}
                value={selectedDept}
                onChange={(_, v) => { setSelectedDept(v); setSelectedGroup(null); setSelectedMaterial(null); setSelectedMakes([]); }}
                renderInput={(p) => <TextField {...p} label="Department" inputProps={{ ...p.inputProps, autoComplete: 'off' }} />}
              />
            </Grid>

            {/* Group Code — works without dept; auto-fills dept */}
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete size="small" options={groupOptions}
                getOptionLabel={(o) => o.groupCode}
                value={selectedGroup}
                onChange={(_, v) => handleGroupSelect(v)}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Group Code" inputProps={{ ...p.inputProps, autoComplete: 'off' }} />}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{o.groupCode}</Typography>
                      <Typography variant="caption" color="text.secondary">{o.department?.name}</Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>

            {/* Material — server-side search, mandatory */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete size="small" options={materialOptions}
                getOptionLabel={(o) => o.name}
                value={selectedMaterial}
                onChange={(_, v) => handleMaterialSelect(v)}
                onInputChange={(_, v) => setMaterialSearch(v)}
                filterOptions={(x) => x}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Material *" helperText="Type to search" inputProps={{ ...p.inputProps, autoComplete: 'off' }} />}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{o.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {o.materialGroup?.groupCode} · {o.materialGroup?.department?.name}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>

            {/* Makes — multi-select for line chart (only makes with prices for material) */}
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                multiple disableCloseOnSelect size="small"
                options={availableMakes}
                getOptionLabel={(o) => o.name}
                value={selectedMakes}
                onChange={(_, v) => setSelectedMakes(v)}
                disabled={!selectedMaterial}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, o, { selected }) => (
                  <li {...props} key={o.id}>
                    <Checkbox icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      checked={selected} style={{ marginRight: 4 }} />
                    {o.name}
                  </li>
                )}
                renderInput={(p) => (
                  <TextField {...p} label="Makes for Line Chart"
                    helperText={selectedMaterial ? 'Empty = All makes' : 'Select material first'}
                    inputProps={{ ...p.inputProps, autoComplete: 'off' }} />
                )}
              />
            </Grid>

            {/* Period */}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">Closing Price Period</Typography>
              <ToggleButtonGroup value={period} exclusive onChange={(_, v) => v && setPeriod(v)} size="small" fullWidth sx={{ mt: 0.25 }}>
                <ToggleButton value="weekly" sx={{ fontSize: 11, px: 1 }}>Weekly</ToggleButton>
                <ToggleButton value="monthly" sx={{ fontSize: 11, px: 1 }}>Monthly</ToggleButton>
                <ToggleButton value="quarterly" sx={{ fontSize: 11, px: 1 }}>Quarterly</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          </Grid>

          {selectedMaterial && (
            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">Showing:</Typography>
              <Chip label={selectedMaterial.name} size="small" color="primary" onDelete={() => { setSelectedMaterial(null); setSelectedMakes([]); }} />
              {selectedMakes.map((m) => (
                <Chip key={m.id} label={m.name} size="small" onDelete={() => setSelectedMakes((p) => p.filter((x) => x.id !== m.id))} />
              ))}
              {selectedMakes.length === 0 && <Chip label="All Makes" size="small" variant="outlined" />}
              {latestChange !== null && (
                <Chip
                  icon={latestChange >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                  label={`${latestChange >= 0 ? '+' : ''}${latestChange}% vs prev period`}
                  color={latestChange >= 0 ? 'error' : 'success'} size="small" variant="outlined"
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Chart area */}
      {!selectedMaterial ? (
        <Alert severity="info" icon={<SearchIcon />} sx={{ py: 3 }}>
          <Typography variant="subtitle1" fontWeight={600}>Select a Material to view price charts</Typography>
          <Typography variant="body2">
            Use the filters above to search for a material. Charts will show closing price trends and current prices per make.
          </Typography>
        </Alert>
      ) : chartLoading ? (
        <LoadingSpinner message="Loading price data..." />
      ) : (
        <Grid container spacing={3}>
          {/* Current Prices Bar Chart */}
          <Grid item xs={12} lg={5}>
            <Card>
              <CardHeader
                title="Current Price by Make"
                subheader={`Latest price per make · ${selectedMaterial.name}`}
                titleTypographyProps={{ fontWeight: 700, color: '#1565C0' }}
              />
              <Divider />
              <CardContent>
                {currentPriceBarData.length === 0 ? (
                  <Typography color="text.secondary" textAlign="center" py={4}>No prices for this material</Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={currentPriceBarData} layout="vertical"
                      margin={{ top: 5, right: 60, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tickFormatter={(v) => `₹${v}`} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="make" width={90} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => [fmt(v), 'Price']} />
                      <Bar dataKey="price" radius={[0, 4, 4, 0]} maxBarSize={36}>
                        {currentPriceBarData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                        <LabelList dataKey="price" position="right" formatter={(v) => fmt(v)}
                          style={{ fontSize: 11, fill: '#333' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Closing Price Trend Line Chart */}
          <Grid item xs={12} lg={7}>
            <Card>
              <CardHeader
                title="Closing Price Trend"
                subheader={`Last price on period-end date · ${period} · ${selectedMakes.length === 0 ? 'All Makes' : selectedMakes.map((m) => m.name).join(', ')}`}
                titleTypographyProps={{ fontWeight: 700, color: '#1565C0' }}
              />
              <Divider />
              <CardContent>
                {lineChart.data.length === 0 ? (
                  <Typography color="text.secondary" textAlign="center" py={4}>No price history for this selection</Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={lineChart.data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${v}`} />
                      <Tooltip formatter={(v, name) => [fmt(v), name]}
                        labelFormatter={(l) => `Period end: ${l}`} />
                      <Legend />
                      {lineChart.keys.map((key, i) => (
                        <Line key={key} type="monotone" dataKey={key}
                          stroke={COLORS[i % COLORS.length]} strokeWidth={2}
                          dot={{ r: 4 }} activeDot={{ r: 6 }} connectNulls />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
